<template>
  <rb-input-base
    ref="inputRef"
    :model-value="textContent"
    :rules="computedRules"
    validate-on="lazy"
    hide-details="auto"
    class="rb-rich-text"
  >
    <template #default="{ isValid }">
      <div class="editor-wrapper bg-neutral-98" :class="{ 'is-focused': isFocused, 'is-error': isValid.value === false }">
        <label
          v-if="label"
          class="editor-label"
          :class="{ 'is-floating': isFocused || hasContent }"
        >
          {{ label }} <span v-if="required || rules.length">*</span>
        </label>

        <editor-content :editor="editor" class="editor-content" />
      </div>

      <div class="editor-toolbar bg-neutral-98" v-if="editor">
        <v-menu transition="slide-y-transition">
          <template v-slot:activator="{ props }">
            <div v-bind="props" class="toolbar-selects d-flex align-center pr-2">
              <span class="text-caption text-neutral-60 mr-1 text-truncate" style="max-width: 100px; min-width: 80px;">
                {{ editor.getAttributes('textStyle').fontFamily || 'Padrão do sistema' }}
              </span>
              <v-icon size="small" color="neutral-60">mdi-chevron-down</v-icon>
            </div>
          </template>
          <v-list density="compact" max-height="300">
            <v-list-item @click="editor.chain().focus().unsetFontFamily().run()">Padrão Sistema</v-list-item>
            <v-list-item
              v-for="font in fonts"
              :key="font"
              @click="editor.chain().focus().setFontFamily(font).run()"
            >
              <span :style="{ fontFamily: font }">{{ font }}</span>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-menu transition="slide-y-transition">
          <template v-slot:activator="{ props }">
            <div v-bind="props" class="toolbar-selects d-flex align-center pr-2 ml-2">
              <span class="text-caption text-neutral-60 mr-1" style="min-width: 35px;">
                {{ editor.getAttributes('textStyle').fontSize || '12pt' }}
              </span>
              <v-icon size="small" color="neutral-60">mdi-chevron-down</v-icon>
            </div>
          </template>
          <v-list density="compact" max-height="300">
            <v-list-item
              v-for="size in ['8pt', '10pt', '12pt', '14pt', '16pt', '18pt', '24pt', '30pt', '36pt', '48pt', '60pt', '72pt']"
              :key="size"
              @click="editor.chain().focus().setFontSize(size).run()"
            >
              <v-list-item-title :style="{ fontSize: size }">{{ size }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <button
          type="button"
          @click="editor.chain().focus().toggleBold().run()"
          :class="{ 'is-active': editor.isActive('bold') }"
        >
          <v-icon>mdi-format-bold</v-icon>
        </button>

        <button
          type="button"
          @click="editor.chain().focus().toggleItalic().run()"
          :class="{ 'is-active': editor.isActive('italic') }"
        >
          <v-icon>mdi-format-italic</v-icon>
        </button>

        <button
          type="button"
          @click="editor.chain().focus().toggleUnderline().run()"
          :class="{ 'is-active': editor.isActive('underline') }"
        >
          <v-icon>mdi-format-underline</v-icon>
        </button>

        <button
          type="button"
          @click="setLink"
          :class="{ 'is-active': editor.isActive('link') }"
        >
          <v-icon>mdi-link-variant</v-icon>
        </button>

        <button
          type="button"
          @click="editor.chain().focus().clearNodes().unsetAllMarks().run()"
        >
          <v-icon>mdi-format-clear</v-icon>
        </button>

        <div class="color-picker-wrapper">
          <input
            type="color"
            @input="editor.chain().focus().setColor($event.target.value).run()"
            :value="editor.getAttributes('textStyle').color || '#000000'"
            class="color-input"
          />
          <button type="button">
            <v-icon :color="editor.getAttributes('textStyle').color">mdi-format-color-text</v-icon>
          </button>
        </div>

        <button
          type="button"
          @click="editor.chain().focus().setTextAlign('left').run()"
          :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }"
        >
          <v-icon>mdi-format-align-left</v-icon>
        </button>

        <button
          type="button"
          @click="editor.chain().focus().setTextAlign('center').run()"
          :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }"
        >
          <v-icon>mdi-format-align-center</v-icon>
        </button>

        <button
          type="button"
          @click="editor.chain().focus().setTextAlign('right').run()"
          :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }"
        >
          <v-icon>mdi-format-align-right</v-icon>
        </button>

        <button
          type="button"
          @click="editor.chain().focus().setTextAlign('justify').run()"
          :class="{ 'is-active': editor.isActive({ textAlign: 'justify' }) }"
        >
          <v-icon>mdi-format-align-justify</v-icon>
        </button>

        <button
          type="button"
          @click="editor.chain().focus().toggleCode().run()"
          :class="{ 'is-active': editor.isActive('code') }"
        >
          <v-icon>mdi-code-tags</v-icon>
        </button>
      </div>
    </template>
  </rb-input-base>
</template>

<script setup>
import { onBeforeUnmount, watch, defineProps, defineEmits, ref, computed } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import { Extension } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Link from '@tiptap/extension-link'
import { TextStyle } from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import FontFamily from '@tiptap/extension-font-family'

const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() { return { types: ['textStyle'] } },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize?.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.fontSize) return {}
              return { style: `font-size: ${attributes.fontSize}` }
            },
          },
        },
      },
    ]
  },
  addCommands() {
    return {
      setFontSize: fontSize => ({ chain }) => {
        return chain().setMark('textStyle', { fontSize }).run()
      },
      unsetFontSize: () => ({ chain }) => {
        return chain().setMark('textStyle', { fontSize: null }).run()
      },
    }
  },
})

const fonts = ['Arial', 'Helvetica', 'Verdana', 'Trebuchet MS', 'Times New Roman', 'Georgia', 'Garamond', 'Courier New', 'Consolas']

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
  rules: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:modelValue'])

const inputRef = ref(null)
const isFocused = ref(false)

const hasContent = computed(() => !!editor.value?.getText()?.trim())
const textContent = computed(() => editor.value?.getText()?.trim() || '')

const computedRules = computed(() => {
  const result = [...props.rules]
  if (props.required) result.push(v => !!v || 'Campo obrigatório.')
  return result
})

const editor = useEditor({
  content: props.modelValue,
  extensions: [
    StarterKit,
    Underline,
    TextStyle,
    Color,
    FontFamily,
    FontSize,
    Link.configure({
      openOnClick: false,
    }),
    TextAlign.configure({
      types: ['heading', 'paragraph'],
      defaultAlignment: 'left',
    }),
  ],
  onFocus: () => {
    isFocused.value = true
  },
  onBlur: () => {
    isFocused.value = false
    inputRef.value?.validate()
  },
  onUpdate: ({ editor }) => {
    emit('update:modelValue', editor.getHTML())
    if (inputRef.value?.isValid === false) {
      inputRef.value.validate()
    }
  },
})

watch(() => props.modelValue, (value) => {
  const isSame = editor.value.getHTML() === value
  if (isSame) return
  editor.value.commands.setContent(value, false)
})

onBeforeUnmount(() => {
  editor.value.destroy()
})

const setLink = () => {
  const previousUrl = editor.value.getAttributes('link').href
  const url = window.prompt('Insira a URL:', previousUrl) ?? ''

  if (url === '') {
    editor.value.chain().focus().extendMarkRange('link').unsetLink().run()
    return
  }

  editor.value.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
}
</script>

<style lang="scss" scoped>
.rb-rich-text {
  :deep(.v-input__control) {
    display: flex;
    flex-direction: column;
  }

  .editor-wrapper {
    border-radius: 4px 4px 0 0;
    position: relative;
    padding: 24px 16px 8px;
    min-height: 100px;
    display: flex;
    background-color: #f5f5f5;
    flex-direction: column;
    text-align: left;
    resize: vertical;
    overflow: auto;
    border-bottom: 1px solid rgb(var(--v-theme-neutral-80));
    transition: border-color 0.2s ease, border-width 0.2s ease, background-color 0.2s ease;

    &:hover {
      background-color: #e5e6e5 !important;
    }

    &.is-focused {
      border-bottom: 2px solid rgb(var(--v-theme-primary-60));
    }

    &.is-error {
      border-bottom: 2px solid rgb(var(--v-theme-error));

      .editor-label {
        color: rgb(var(--v-theme-error));
      }
    }

    .editor-label {
      position: absolute;
      top: 16px;
      left: 16px;
      font-size: 16px;
      color: rgb(var(--v-theme-neutral-35));
      pointer-events: none;
      transform-origin: left top;
      transition:
        top 0.2s ease,
        font-size 0.2s ease,
        color 0.2s ease;

      &.is-floating {
        top: 8px;
        font-size: 12px;
      }
    }

    &.is-focused .editor-label.is-floating {
      color: rgb(var(--v-theme-primary-60));
    }

    .editor-content {
      display: flex;
      flex: 1;
      min-height: 100px;
    }

    :deep(.ProseMirror) {
      font-family: Roboto, sans-serif;
      font-size: 16px;
      color: rgb(var(--v-theme-neutral-10));
      outline: none;
      flex-grow: 1;
      min-height: 100%;
      cursor: text;

      p {
        margin-bottom: 0.5em;
      }

      p.is-editor-empty:first-child::before {
        content: attr(data-placeholder);
        float: left;
        width: 100%;
        height: 0;
        pointer-events: none;
        color: rgb(var(--v-theme-neutral-60));
        text-align: left;
      }
    }
  }

  .editor-toolbar {
    border-radius: 0 0 4px 4px;
    display: flex;
    align-items: center;
    padding: 4px 8px;
    flex-wrap: wrap;
    max-width: fit-content;

    button {
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 6px;
      border-radius: 4px;
      color: rgb(var(--v-theme-neutral-35));
      display: flex;
      align-items: center;
      justify-content: center;
      transition:
        background 0.2s ease,
        color 0.2s ease;

      &:hover {
        background: rgba(var(--v-theme-neutral-80), 0.2);
      }

      &.is-active {
        color: rgb(var(--v-theme-primary-60));
        background: rgba(var(--v-theme-primary-60), 0.1);
      }
    }

    .toolbar-selects {
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      &:hover {
        background: rgba(var(--v-theme-neutral-80), 0.2);
      }
    }

    .color-picker-wrapper {
      position: relative;
      display: flex;

      .color-input {
        position: absolute;
        opacity: 0;
        width: 100%;
        height: 100%;
        cursor: pointer;
        z-index: 2;
      }

      button {
        z-index: 1;
      }
    }
  }
}
</style>
